import React from 'react';
import styled from '@2pulse/responsive';
import { DateTime } from 'luxon';
import { FilterButton } from '@app/components';
import { extractApiGenericError, normalize } from '@app/helpers';
import { useToast } from '@app/hooks';
import { fiscalApi } from '@app/store';
import { useAppSelector } from '@app/store/hooks';
import type { IFiscalAccountingExportSummary } from '@tickeat/types';

// LE SCEAU ET LA PISTE D'AUDIT DES EXPORTS COMPTABLES (E-07 volet 2, 6.14.2.1/6.14.2.2,
// chantier lot 6 tâche 14). Restitution en lecture seule de `fiscal_accounting_exports`
// (append-only, tâche 13, `dashboard::fiscal.read`) : chaque tentative d'export d'une
// boutique — scellée ou refusée — laisse une ligne ici, jamais réécrite.
//
// LE VOLET DÉBIT N'EST JAMAIS RAPPROCHÉ (I5, lot 6 tâche 12) : la GT_JOUR signée ne porte
// que le TTC par taux, aucune ventilation par moyen de paiement. Ce panneau ne dit donc
// jamais qu'un export est « authentifié » — seulement « rapproché sur le TTC par taux ».
const dateFr = (iso: string) => DateTime.fromISO(iso).toFormat('dd/MM/yyyy HH:mm');
const dateFrGdh = (gdh: string) => DateTime.fromFormat(gdh, 'yyyyMMddHHmmss').toFormat('dd/MM/yyyy');

const LIBELLE_PERIMETRE: Record<string, string> = {
  // Jamais « authentifié » : seul le crédit (TVA/TTC par taux) est confronté au Grand Total
  // signé, la GT_JOUR ne portant aucune ventilation par moyen de paiement (I5, tâche 12).
  CREDIT_PAR_TAUX: 'Rapproché sur le TTC par taux (crédit uniquement)',
};
const libellePerimetre = (perimetre: string): string => LIBELLE_PERIMETRE[perimetre] ?? `Rapproché — ${perimetre}`;

// Raisons de non-scellement (tâche 13) : `AUCUN_REGISTRE` accompagne un fichier PARTI (mais
// non rapproché, jamais scellé) ; les trois autres sont les refus 422 structurés de la
// tâche 12 — aucun fichier n'a existé pour ceux-là.
const LIBELLE_RAISON: Record<string, string> = {
  AUCUN_REGISTRE: "Boutique sans registre fiscal actif : le fichier est parti sans registre à confronter.",
  PERIODE_NON_CLOTUREE: 'Refusé : période non clôturée (6.14.2.4).',
  DIVERGENCE_GRAND_TOTAL: 'Refusé : divergence avec le Grand Total signé.',
  FENETRE_NON_ALIGNEE: "Refusé : la fenêtre exportée ne coïncidait pas avec un Z de caisse.",
};
const libelleRaison = (raison: string): string => LIBELLE_RAISON[raison] ?? `Refusé (${raison})`;

export const FiscalAccountingExports = () => {
  const toast = useToast();
  // Sans boutique sélectionnée, la gateway n'a pas d'en-tête `x-shop` : on ne demande rien
  // plutôt que d'afficher « Bad Request Exception » (même garde que les autres panneaux de
  // la page Conformité).
  const boutique = useAppSelector((state) => state.app.selectedShop);
  const { data: traces, isFetching, isError, error } = fiscalApi.useReadFiscalAccountingExportsQuery(undefined, { skip: !boutique });
  const liste = traces ?? [];

  const copier = async (valeur: string) => {
    try { await navigator.clipboard.writeText(valeur); toast.success('Empreinte copiée.'); }
    catch { toast.error('Impossible de copier.'); }
  };

  if (isError) {
    return (
      <Bloc testID="exports-comptables">
        <Titre>Exports comptables</Titre>
        <Alerte testID="exports-comptables-erreur">
          Impossible de charger les exports comptables : {extractApiGenericError(error as never) ?? 'erreur réseau'}.
        </Alerte>
      </Bloc>
    );
  }

  return (
    <Bloc testID="exports-comptables">
      <Titre>Exports comptables <Compteur>({liste.length})</Compteur></Titre>
      <Rappel>
        Chaque tentative d'export — aboutie ou refusée — est tracée ici. L'authenticité du
        fichier téléchargé porte sur le crédit (TTC par taux) ; vérifiez-la avec la clé
        publique du Trousseau de signature Tickeat, publiée plus bas sur cette page. Le fichier
        « …-scellement.json » téléchargé avec chaque export porte l'empreinte, la signature, le
        keyId et le format de la trace (formatVersion) — comparables à ceux de la ligne
        correspondante ci-dessous — ainsi que son auteur, lui déclaré par le tableau de bord
        (« declarePar »), jamais une donnée signée.
      </Rappel>

      {!traces && isFetching && <Rappel>Chargement…</Rappel>}

      {traces && liste.length === 0 && (
        <Rappel>Aucun export comptable n'a encore été produit pour ce restaurant.</Rappel>
      )}

      {liste.map((trace, index) => (
        <Carte key={trace._id} testID={`export-${index}`}>
          <EnTete>
            <Pastille ok={trace.scelle}>{trace.scelle ? 'Scellé' : 'Non scellé'}</Pastille>
            <DateTexte>le {dateFr(trace.constitutedIso)}</DateTexte>
          </EnTete>

          <Ligne>
            <Cle>Auteur</Cle>
            <Valeur>{trace.userName ?? trace.userId ?? 'Utilisateur inconnu'}</Valeur>
          </Ligne>

          <Ligne>
            <Cle>Période</Cle>
            <Valeur>{dateFrGdh(trace.fromGdh)} → {dateFrGdh(trace.toGdh)}</Valeur>
          </Ligne>

          <Ligne>
            <Cle>Périmètre</Cle>
            <Valeur>{libellePerimetre(trace.perimetre)}</Valeur>
          </Ligne>

          {trace.registerCodes.length > 0 && (
            <Ligne>
              <Cle>Caisses</Cle>
              <Valeur>{trace.registerCodes.join(', ')}</Valeur>
            </Ligne>
          )}

          {trace.cloturesNF525.length > 0 && (
            <Ligne>
              <Cle>Clôtures fiscales</Cle>
              <Valeur>{trace.cloturesNF525.join(' → ')}</Valeur>
            </Ligne>
          )}

          {!trace.scelle && trace.raison && (
            <Alerte testID={`raison-refus-${index}`}>{libelleRaison(trace.raison)}</Alerte>
          )}

          {trace.sha256 && (
            <Ligne>
              <Cle>Empreinte SHA-256 {trace.scelle ? '(scellée)' : '(descriptive, non signée)'}</Cle>
              <LigneValeur>
                <Empreinte selectable testID={`sha256-${index}`}>{trace.sha256}</Empreinte>
                <FilterButton title="Copier" onPress={() => copier(trace.sha256 as string)} testID={`copier-sha256-${index}`} />
              </LigneValeur>
            </Ligne>
          )}

          {trace.keyId && (
            <Ligne>
              <Cle>keyId</Cle>
              <Valeur>{trace.keyId}</Valeur>
            </Ligne>
          )}

          {trace.formatVersion !== null && (
            <Ligne>
              <Cle>Format de la trace</Cle>
              <Valeur testID={`format-version-${index}`}>v{trace.formatVersion}</Valeur>
            </Ligne>
          )}
        </Carte>
      ))}
    </Bloc>
  );
};

const Bloc = styled.View(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.colors.translucid,
  borderRadius: normalize(42),
  padding: normalize(21),
  marginBottom: normalize(21),
  rowGap: normalize(13),
}));

const Titre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '700',
  color: theme.colors.babypowder,
}));

const Compteur = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '700',
  color: theme.colors.gold,
}));

const Rappel = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  color: theme.colors.grey,
}));

const Alerte = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  color: theme.colors.crimson,
}));

const Carte = styled.View(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.colors.translucid,
  borderRadius: normalize(28),
  padding: normalize(16),
  rowGap: normalize(8),
}));

const EnTete = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: normalize(13),
});

const Pastille = styled.Text<{ ok: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '700',
  color: theme.colors.babypowder,
  backgroundColor: props.ok ? '#2AA831' : theme.colors.crimson,
  borderRadius: normalize(10),
  paddingHorizontal: normalize(12),
  paddingVertical: normalize(4),
  overflow: 'hidden',
}));

const DateTexte = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.grey,
}));

const Ligne = styled.View({ rowGap: normalize(2) });

const Cle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '700',
  color: theme.colors.celadonblue,
}));

const Valeur = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  color: theme.colors.babypowder,
}));

const LigneValeur = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  columnGap: normalize(10),
  flexWrap: 'wrap',
});

const Empreinte = styled.Text(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: normalize(13),
  color: theme.colors.babypowder,
  opacity: 0.85,
  flexShrink: 1,
}));
